import { EventEmitter } from "@angular/core";
import { OrderByPipe, FilterByPipe } from "ngx-pipes";

import * as _ from "lodash";

import { TableRemote } from "./remote";
import { TableColumn } from "./column";
import { TableGroup } from "./group";
import { TableRow } from "./row";
import { TableItem } from "./item";

import {
  TableSettings,
  TableCache,
  TableRemoteData,
  TableColumnData,
  TableGroupData,
  TableFilter,
  TableSort,
  TablePagination,
  TablePaginationSize,
  TableTotal,
  TableTotalData,
  TableRowEvent,
  TableRowClickableFunction,
  TableRowSelectableFunction,
  TableRowCssClassFunction,
  TableRowStyleFunction
} from "../table.interface";

export class Table {
  constructor(
    private _tableSettings: TableSettings,
    private _orderBy: OrderByPipe,
    private _filterBy: FilterByPipe
  ) {
    this.setColumns(this._tableSettings.columns, true);
    if (!this._tableSettings.sort) {
      this.setSort(null, true);
    }
    if (!this._tableSettings.pagination) {
      this.setPagination(null, true);
    }
    if (_.isBoolean(this._tableSettings.loading)) {
      this.loading = this._tableSettings.loading;
    }
    this._remote = new TableRemote(this._tableSettings, this);
    this._loadCache();
    if (
      !this._remote.valid ||
      (this._remote.valid && this._remote.initial_loading)
    ) {
      this.update();
    }
  }

  public get id(): string {
    return this._tableSettings.id;
  }

  public get cache(): boolean {
    return this._tableSettings.cache;
  }

  public get remote(): TableRemote {
    return this._remote;
  }

  public get columns(): Array<TableColumn> {
    return this._columns;
  }

  public get raw_data(): Array<any> {
    return this._tableSettings.data;
  }
  public set raw_data(raw_data: Array<any>) {
    this._tableSettings.data = raw_data;
  }

  public get empty_text(): string {
    return this._tableSettings.empty_text
      ? this._tableSettings.empty_text
      : "Nenhum registro encontrado.";
  }
  public set empty_text(empty_text: string) {
    this._tableSettings.empty_text = empty_text;
  }

  public get action_text(): string {
    return this._tableSettings.action_text
      ? this._tableSettings.action_text
      : "AÃ§Ãµes";
  }
  public set action_text(action_text: string) {
    this._tableSettings.action_text = action_text;
  }

  public get selectable(): TableRowSelectableFunction | boolean {
    if (
      _.isBoolean(this._tableSettings.selectable) ||
      _.isFunction(this._tableSettings.selectable)
    ) {
      return this._tableSettings.selectable;
    }
    return false;
  }
  public set selectable(selectable: TableRowSelectableFunction | boolean) {
    this._tableSettings.selectable = selectable;
  }

  public get clickable(): TableRowClickableFunction | boolean {
    if (
      _.isBoolean(this._tableSettings.clickable) ||
      _.isFunction(this._tableSettings.clickable)
    ) {
      return this._tableSettings.clickable;
    }
    return false;
  }
  public set clickable(clickable: TableRowClickableFunction | boolean) {
    this._tableSettings.clickable = clickable;
  }

  public get row_class(): TableRowCssClassFunction | string {
    return this._tableSettings.row_class;
  }

  public get row_style(): TableRowStyleFunction | Object {
    return this._tableSettings.row_style;
  }

  public get totals(): Array<TableTotal> {
    return this._totals;
  }

  public get group(): TableGroupData {
    return this._tableSettings.group;
  }
  public get is_grouped(): boolean {
    return this._is_grouped;
  }

  public get filter(): TableFilter {
    if (this._tableSettings.filter) {
      return this._tableSettings.filter;
    }
    return {
      value: null,
      accessor: []
    };
  }

  public get sort(): TableSort {
    const sort = this._tableSettings.sort;
    if (sort && sort.accessor && sort.direction && sort.icon) {
      return sort;
    } else {
      this.setSort(sort, true);
      return sort;
    }
  }

  public get pagination(): TablePagination {
    if (this._tableSettings.pagination) {
      return _.merge(
        {},
        this._getDefaultPagination(),
        this._tableSettings.pagination
      );
    }
    return this._getDefaultPagination();
  }

  public get all_selected_icon(): string {
    return this._all_selected_icon;
  }

  private _remote: TableRemote;

  private _cache: TableCache;

  private _columns: Array<TableColumn> = [];
  private _totals: Array<TableTotal> = [];

  private _is_grouped = false;
  private _all_selected_icon = "check_box_outline_blank";

  public onUpdateStart: EventEmitter<void> = new EventEmitter();
  public onUpdateError: EventEmitter<void> = new EventEmitter();
  public onUpdateSuccess: EventEmitter<void> = new EventEmitter();
  public onUpdateEnd: EventEmitter<void> = new EventEmitter();

  public onCacheLoaded: EventEmitter<TableCache> = new EventEmitter();
  public onDataChange: EventEmitter<any[]> = new EventEmitter();
  public onGroupChange: EventEmitter<TableGroupData> = new EventEmitter();
  public onTotalsChange: EventEmitter<TableTotalData[]> = new EventEmitter();
  public onPaginationChange: EventEmitter<TablePagination> = new EventEmitter();
  public onFilterChange: EventEmitter<TableFilter> = new EventEmitter();
  public onSortChange: EventEmitter<TableSort> = new EventEmitter();
  public onSelectionChange: EventEmitter<any[]> = new EventEmitter();

  public onRowClick: EventEmitter<any> = new EventEmitter();

  public loading = true;

  public data: Array<TableGroup | TableRow> = [];

  public getCacheData(): TableCache {
    return this._cache;
  }
  public setRemote(
    remote: TableRemoteData,
    skip_update: boolean = false
  ): boolean {
    if (!_.isEqual(remote, this._tableSettings.remote)) {
      this._tableSettings.remote = remote;
      this._remote = new TableRemote(this._tableSettings, this);
      if (!skip_update) {
        this.update();
      }
      return true;
    }
    return false;
  }

  public setColumns(
    columns: Array<TableColumnData | null>,
    skip_update: boolean = false
  ): boolean {
    const new_columns: Array<TableColumn> = columns
      .filter(columns => !!columns)
      .map(column => new TableColumn(column, this));

    if (this._tableSettings.selectable) {
      new_columns.unshift(
        new TableColumn(
          {
            selectable: true,
            header: {
              label: "Selecionar todos"
            }
          },
          this
        )
      );
    }

    if (_.isEqual(new_columns, this._columns)) return false;

    this._columns = new_columns;

    if (!skip_update) this.update();

    return true;
  }
  public setData(
    raw_data: Array<any>,
    skip_update: boolean = false
  ): Promise<Table> {
    return new Promise(resolve => {
      if (!_.isEqual(raw_data, this.raw_data)) {
        this.raw_data = _.cloneDeep(raw_data);
        this.loading = false;
        if (!skip_update) {
          this.update().then(() => {
            resolve(this);
          });
          return;
        }
      }
      resolve(this);
    });
  }
  public setTotals(
    totals: Array<TableTotalData>,
    skip_update: boolean = false
  ): Promise<Table> {
    return new Promise(resolve => {
      if (!_.isEqual(totals, this._tableSettings.totals)) {
        this._tableSettings.totals = totals;
        if (!skip_update) {
          this.update().then(() => {
            resolve(this);
            this.onTotalsChange.emit(this._tableSettings.totals);
          });
          return;
        }
        this.onTotalsChange.emit(this._tableSettings.totals);
      }
      resolve(this);
    });
  }
  public setGroup(
    group: TableGroupData,
    skip_update: boolean = false
  ): Promise<Table> {
    return new Promise(resolve => {
      if (!_.isEqual(group, this._tableSettings.group)) {
        this._tableSettings.group = group;
        if (!skip_update) {
          this.update().then(() => {
            resolve(this);
            this.onGroupChange.emit(this._tableSettings.group);
          });
          return;
        }
        this.onGroupChange.emit(this._tableSettings.group);
      }
      resolve(this);
    });
  }
  public setFilter(
    filter: TableFilter,
    skip_update: boolean = false
  ): Promise<Table> {
    return new Promise(resolve => {
      if (!_.isEqual(filter, this._tableSettings.filter)) {
        this._tableSettings.filter = filter;
        if (!this._tableSettings.pagination.disabled) {
          this._tableSettings.pagination.number = 1;
        }
        if (!skip_update) {
          this.update().then(() => {
            resolve(this);
            this.onFilterChange.emit(this.filter);
          });
          return;
        }
        this.onFilterChange.emit(this.filter);
      }
      resolve(this);
    });
  }
  public setSort(
    sort: TableSort,
    skip_update_or_event: boolean = false
  ): Promise<Table> {
    return new Promise(resolve => {
      const default_sort = this._getDefaultSort();
      const new_sort = _.merge(
        {},
        default_sort,
        this._tableSettings.sort,
        sort
      );
      if (!_.isEqual(new_sort, this._tableSettings.sort)) {
        this._tableSettings.sort = {
          manual: new_sort.manual,
          accessor: new_sort.accessor,
          direction: new_sort.direction,
          icon: new_sort.icon
            ? new_sort.icon
            : this._getSortDirectionIcon(new_sort)
        };
        if (!skip_update_or_event && !this._tableSettings.sort.manual) {
          this.update().then(() => {
            resolve(this);
            this.onSortChange.emit(this.sort);
          });
          return;
        }
        if (
          !this._tableSettings.sort.manual ||
          (this._tableSettings.sort.manual && !skip_update_or_event)
        ) {
          this.onSortChange.emit(this.sort);
        }
      }
      resolve(this);
    });
  }
  public setPagination(
    page: TablePagination,
    skip_update_or_event: boolean = false
  ): Promise<Table> {
    return new Promise(resolve => {
      const new_page = _.merge({}, this.pagination, page);
      if (!_.isEqual(new_page, this._tableSettings.pagination)) {
        this._tableSettings.pagination = new_page;
        if (!skip_update_or_event && !this._tableSettings.pagination.manual) {
          this.update().then(() => {
            resolve(this);
            this.onPaginationChange.emit(this.pagination);
          });
          return;
        }
        if (
          !this._tableSettings.pagination.manual ||
          (this._tableSettings.pagination.manual && !skip_update_or_event)
        ) {
          this.onPaginationChange.emit(this.pagination);
        }
      }
      resolve(this);
    });
  }
  public setPageSize(
    size: TablePaginationSize,
    skip_update_or_event: boolean = false
  ): Promise<Table> {
    return new Promise(resolve => {
      if (size != this.pagination.size) {
        this._tableSettings.pagination.size = size;
        if (!skip_update_or_event && !this._tableSettings.pagination.manual) {
          this.update().then(() => {
            resolve(this);
            this.onPaginationChange.emit(this.pagination);
          });
          return;
        }
        if (
          !this._tableSettings.pagination.manual ||
          (this._tableSettings.pagination.manual && !skip_update_or_event)
        ) {
          this.onPaginationChange.emit(this.pagination);
        }
      }
      resolve(this);
    });
  }
  public setPageNumber(
    number: number,
    skip_update_or_event: boolean = false
  ): Promise<Table> {
    return new Promise(resolve => {
      if (number != this.pagination.number) {
        this._tableSettings.pagination.number = number;
        if (!skip_update_or_event && !this._tableSettings.pagination.manual) {
          this.update().then(() => {
            resolve(this);
            this.onPaginationChange.emit(this.pagination);
          });
          return;
        }
        if (
          !this._tableSettings.pagination.manual ||
          (this._tableSettings.pagination.manual && !skip_update_or_event)
        ) {
          this.onPaginationChange.emit(this.pagination);
        }
      }
      resolve(this);
    });
  }

  public getRowSelectable(row_data: TableRow): boolean {
    if (_.isBoolean(this.selectable)) {
      return <boolean>this.selectable;
    } else {
      const event: TableRowEvent = {
        table_row: row_data,
        data: row_data.getData()
      };
      return (<TableRowSelectableFunction>this.selectable)(event)
        ? true
        : false;
    }
  }

  public getRowClickable(row_data: TableRow): boolean {
    if (_.isBoolean(this.clickable)) {
      return <boolean>this.clickable;
    } else {
      const event: TableRowEvent = {
        table_row: row_data,
        data: row_data.getData()
      };
      return (<TableRowClickableFunction>this.clickable)(event) ? true : false;
    }
  }

  public getRowCssClass(row_data: TableRow): string {
    if (_.isString(this.row_class)) {
      return <string>this.row_class;
    } else if (_.isFunction(this.row_class)) {
      const event: TableRowEvent = {
        table_row: row_data,
        data: row_data.getData()
      };
      return (<TableRowCssClassFunction>this.row_class)(event) || null;
    }

    return null;
  }

  public getRowStyle(row_data: TableRow): Object {
    if (_.isPlainObject(this.row_style)) {
      return <Object>this.row_style;
    } else if (_.isFunction(this.row_style)) {
      const event: TableRowEvent = {
        table_row: row_data,
        data: row_data.getData()
      };
      const response = (<TableRowStyleFunction>this.row_style)(event);
      return _.isPlainObject(response) ? response : {};
    }

    return {};
  }

  public update(): Promise<Table> {
    return new Promise(resolve => {
      this.onUpdateStart.emit();

      this._saveCache();
      this._setAttributes();
      this.updateAllSelectedIcon();

      if (this.remote && this.remote.valid) {
        this._updateRemote(() => resolve(this));
      } else {
        this._updateLocal(() => resolve(this));
      }
    });
  }

  public updateSort(
    column: TableColumn,
    skip_update: boolean = false
  ): Promise<Table> {
    return new Promise(resolve => {
      if (!column.sortable) {
        resolve(this);
        return;
      }
      const new_sort: TableSort = this._tableSettings.sort
        ? _.cloneDeep(this._tableSettings.sort)
        : this._getDefaultSort();
      if (column.accessor === this.sort.accessor) {
        new_sort.direction = new_sort.direction === "asc" ? "desc" : "asc";
      } else {
        new_sort.accessor = column.accessor;
      }
      new_sort.icon = this._getSortDirectionIcon(new_sort);
      if (!_.isEqual(this._tableSettings.sort, new_sort)) {
        this._tableSettings.sort = new_sort;
        if (this._tableSettings.sort.manual) {
          this.loading = true;
        }
        if (!skip_update && !this._tableSettings.sort.manual) {
          this.update().then(() => {
            resolve(this);
            this.onSortChange.emit(this._tableSettings.sort);
          });
          return;
        }
      }
      resolve(this);
    });
  }

  public updateTotals(): Array<TableTotal> {
    this._totals = [];
    const totals = !_.isEmpty(this._tableSettings.totals)
      ? this._tableSettings.totals
      : [];

    if (totals.length == 0) return;

    let colspan = 1;
    let total_cols = 0;
    this._totals = this.columns
      .map(
        (column): TableTotal => {
          const total_column = totals.filter(
            total_column => total_column.accessor == column.accessor
          )[0];

          if (total_column) {
            const label = total_column.format
              ? total_column.format(this.getRawData(), this.getData())
              : "";

            const title = total_column.title
              ? total_column.title(this.getRawData(), this.getData())
              : label.replace(/<(?:.|\n)*?>/gm, "");

            const numeric = _.isBoolean(total_column.numeric)
              ? total_column.numeric
              : column.numeric;

            if (total_column.colspan && total_column.colspan > 1) {
              colspan = total_column.colspan;
            } else if (colspan > 1) {
              colspan--;
            } else {
              colspan = 1;
            }

            total_cols += colspan;

            return {
              label,
              title,
              colspan,
              html: total_column.html,
              numeric
            };
          } else {
            if (total_cols >= this._columns.length) {
              return null;
            }

            if (colspan > 1) {
              colspan--;
              return null;
            }

            total_cols++;

            return {
              label: "",
              title: "",
              colspan: 1,
              html: false,
              numeric: column.numeric
            };
          }
        }
      )
      .filter(total => !!total);

    return this._totals;
  }

  public updateColumns() {
    this._columns.forEach(column => column.update());
  }

  public hasColumns(): boolean {
    return this.columns && this.columns && this.columns.length > 0;
  }

  public hasSelectableRows(): boolean {
    let data = this.getData();
    if (data.length == 0) {
      return false;
    }

    data = data.filter(row => row.selectable);
    return data.length > 0;
  }

  public getSortIcon(column: TableColumn): string {
    return column.accessor === this.sort.accessor ? this.sort.icon : "sort";
  }

  public decreasePage(): Promise<Table> {
    return new Promise(resolve => {
      if (!this.pagination.previous) {
        resolve(this);
        return;
      }
      this._tableSettings.pagination.number = this.pagination.previous;
      this._tableSettings.pagination.previous =
        this.pagination.previous > 1 ? this.pagination.previous - 1 : null;
      this._tableSettings.pagination.next = this.pagination.next - 1;
      if (!this.pagination.manual) {
        this.update().then(() => {
          resolve(this);
          this.onPaginationChange.emit(this.pagination);
        });
        return;
      } else {
        this.loading = true;
      }
      resolve(this);
      this.onPaginationChange.emit(this.pagination);
    });
  }

  public increasePage(): Promise<Table> {
    return new Promise(resolve => {
      if (!this.pagination.next) {
        resolve(this);
        return;
      }
      this._tableSettings.pagination.number = this.pagination.next;
      this._tableSettings.pagination.next =
        this.pagination.next < this.pagination.last
          ? this.pagination.next + 1
          : null;
      this._tableSettings.pagination.previous = this.pagination.previous + 1;
      if (!this.pagination.manual) {
        this.update().then(() => {
          resolve(this);
          this.onPaginationChange.emit(this.pagination);
        });
        return;
      } else {
        this.loading = true;
      }
      resolve(this);
      this.onPaginationChange.emit(this.pagination);
    });
  }

  public getData(): Array<TableRow> {
    switch (this.is_grouped) {
      case true:
        return _.flatten(
          (<Array<TableGroup>>this.data).map(group => group.getData())
        );
      case false:
        return <Array<TableRow>>this.data;
    }
  }

  public getRawData(): Array<any> {
    switch (this.is_grouped) {
      case true:
        return _.flatten(
          (<Array<TableGroup>>this.data).map(group => group.getRawData())
        );
      case false:
        return (<Array<TableRow>>this.data).map(item => item.getData());
    }
  }

  public getSelectedData(raw_data: boolean = false): Array<any> {
    let data = this.getData();
    if (data.length == 0) {
      return [];
    }

    data = data.filter(item => item.selected);
    return _.flatten(data).map(_item => (raw_data ? _item.getData() : _item));
  }

  public getSelectableData(raw_data: boolean = false): Array<any> {
    let data = this.getData();
    if (data.length == 0) {
      return [];
    }

    data = data.filter(row => row.selectable);
    return _.flatten(data).map(_item => (raw_data ? _item.getData() : _item));
  }

  public updateAllSelectedIcon() {
    const selected_count = this.getSelectedData().length;
    const all_selectable_count = this.getSelectableData().length;
    this._all_selected_icon =
      all_selectable_count > 0 && selected_count == all_selectable_count
        ? "check_box"
        : selected_count > 0
        ? "indeterminate_check_box"
        : "check_box_outline_blank";
  }

  public toggleSelectAll() {
    const selected_count = this.getSelectedData().length;
    const all_selectable_count = this.getSelectableData().length;
    const all_selected = selected_count == all_selectable_count;
    this.getSelectableData().forEach(item =>
      item.setSelected(all_selected ? false : true, true)
    );
    this.onSelectionChange.emit(this.getSelectedData(true));
  }

  public getTrackRowBy(): Function {
    return (index: number, row: TableRow) => {
      const row_data = row.getData();
      return row_data.id ? row_data.id : index;
    };
  }

  public getTrackItemBy(): Function {
    return (index: number, item: TableItem) => {
      return item.accessor || index;
      // const row_data = item.row_data;
      // let accessor_data = item.accessor ? _.at(row_data, item.accessor)[0] : null;
      // if (item.accessor && (!accessor_data || _.isObjectLike(accessor_data))) {
      //   accessor_data = `${item.accessor}_${index}`;
      // }
      // return accessor_data ? accessor_data : index;
    };
  }

  private _afterUpdate() {
    this.updateTotals();
    this.updateColumns();
  }

  private _updateRemote(callback?: Function) {
    this.raw_data = [];
    this.data = [];
    this._totals = [];
    this.loading = true;
    this.remote
      .call()
      .then(data => {
        this.raw_data = data;
        let indexed_data: Array<any> = _.cloneDeep(this.raw_data).map(
          (row_data, index) => {
            row_data.index = index;
            return row_data;
          }
        );
        indexed_data = indexed_data.map(
          (row_data, index: number) =>
            new TableRow(
              this,
              this.columns,
              index,
              this.raw_data[row_data.index]
            )
        );
        this.data = this._applyGroup(indexed_data);
        this._afterUpdate();
        this.loading = false;
        const _data = this.getData();
        if (_.isFunction(callback)) {
          callback(_data);
        }
        this.onDataChange.emit(_data);
        this.onUpdateSuccess.emit();
        this.onUpdateEnd.emit();
      })
      .catch(err => {
        console.warn(err);
        this.raw_data = [];
        this.data = [];
        this._afterUpdate();
        this.loading = false;

        const _data = this.getData();
        if (_.isFunction(callback)) {
          callback(_data);
        }

        this.onDataChange.emit(_data);
        this.onUpdateError.emit();
        this.onUpdateEnd.emit();
      });
  }

  private _updateLocal(callback?: Function) {
    if (_.isEmpty(this.raw_data)) {
      this.data = [];
      this._afterUpdate();

      const _data = this.getData();
      if (_.isFunction(callback)) {
        callback(_data);
      }

      this.onDataChange.emit(_data);
      this.onUpdateSuccess.emit();
      this.onUpdateEnd.emit();

      return;
    }

    let filtered_data: Array<any>;
    const filter_value: any = _.isFunction(this.filter.value)
      ? this.filter.value()
      : this.filter.value;

    // Set index to bind with real data
    const indexed_data: Array<any> = _.cloneDeep(this.raw_data).map(
      (row_data, index) => {
        row_data.index = index;
        return row_data;
      }
    );

    if (!_.isEmpty(filter_value) && !_.isEmpty(this.filter.accessor)) {
      filtered_data = this._filterBy.transform(
        indexed_data,
        this.filter.accessor,
        filter_value
      );
    } else {
      filtered_data = indexed_data;
    }

    if (!this.pagination.disabled && !this.pagination.manual) {
      this.pagination.last = Math.floor(
        filtered_data.length / this.pagination.size
      );
      if (this.pagination.last < 1) {
        this.pagination.last = 1;
      }
      if (this.pagination.number > this.pagination.last) {
        this.pagination.number = this.pagination.last;
      }
      this.pagination.next =
        this.pagination.number < this.pagination.last
          ? this.pagination.number + 1
          : null;
      this.pagination.previous =
        this.pagination.number > 0 ? this.pagination.number - 1 : null;
      this.pagination.count = filtered_data.length;

      const start_index =
        (this.pagination.number > 0 ? this.pagination.number - 1 : 0) *
        this.pagination.size;
      const end_index = start_index + this.pagination.size;
      const ordered_data = this._applySort(
        filtered_data,
        start_index,
        end_index
      );
      const instantiated_data = this._createRows(ordered_data);
      this.data = this._applyGroup(instantiated_data);
    } else {
      const ordered_data = this._applySort(filtered_data);
      const instantiated_data = this._createRows(ordered_data);
      this.data = this._applyGroup(instantiated_data);
    }

    this._afterUpdate();

    const _data = this.getData();
    if (_.isFunction(callback)) {
      callback(_data);
    }
    this.onDataChange.emit(_data);
    this.onUpdateSuccess.emit();
    this.onUpdateEnd.emit();
  }

  private _createRows(filtered_data: Array<any>) {
    return filtered_data.map(
      row_data =>
        new TableRow(
          this,
          this.columns,
          row_data.index,
          this.raw_data[row_data.index]
        )
    );
  }

  private _applySort(
    filtered_data: Array<any>,
    page_start_index?: number,
    page_end_index?: number
  ): Array<any> {
    if (this.sort.manual) {
      return filtered_data;
    }
    const ordered_data: Array<any> = this._orderBy.transform(
      filtered_data,
      this._getSortProperty()
    );
    if (page_start_index >= 0 && page_end_index >= 0) {
      return ordered_data.slice(page_start_index, page_end_index);
    }
    return ordered_data;
  }

  private _applyGroup(
    ordered_data: Array<TableRow>
  ): Array<TableGroup> | Array<TableRow> {
    if (!this._is_grouped) {
      return ordered_data;
    }
    const groups: any = _.groupBy(
      ordered_data,
      `_rowData.${this.group.accessor}`
    );
    const parsed_groups: any = _.map(groups, (data, key) => {
      return {
        key: key,
        data: data
      };
    });
    const ordered_groups: Array<any> = _.orderBy(parsed_groups, "key", "asc");
    let prev_group: any;
    const grouped_data = ordered_groups.map(group => {
      const key =
        group.key == "null" || group.key == "undefined" ? null : group.key;
      const instance = new TableGroup(
        this,
        this.group,
        key,
        group.data,
        prev_group
      );
      prev_group = instance;
      return instance;
    });
    return grouped_data;
  }

  private _getSortProperty(): string {
    return `${this._getSortDirectionSignal()}${this.sort.accessor}`;
  }

  private _getSortDirectionSignal(): string {
    return this.sort.direction == "asc" ? "+" : "-";
  }

  private _getSortDirectionIcon(sort: TableSort = this.sort): string {
    return `sort-${sort.direction == "asc" ? "down" : "up"}`;
  }

  private _getDefaultSortAccessor(): string {
    let first_column_sortable: TableColumn;
    this.columns.forEach(column => {
      if (!first_column_sortable && column.sortable) {
        first_column_sortable = column;
      }
    });
    return first_column_sortable
      ? first_column_sortable.accessor
      : this.columns[0].accessor;
  }

  private _getDefaultSort(): TableSort {
    return {
      manual: false,
      direction: "asc",
      accessor: this._getDefaultSortAccessor(),
      icon: "sort-down"
    };
  }

  private _getDefaultPagination(): TablePagination {
    return {
      disabled: false,
      manual: false,
      number: 1,
      last: 1,
      next: null,
      previous: null,
      count: 0,
      size: 50
    };
  }

  private _setAttributes() {
    this._is_grouped = this.group && this.group.accessor ? true : false;
  }

  private _loadCache() {
    if (!this._tableSettings.id || !this._tableSettings.cache) {
      return;
    }
    try {
      this._cache = JSON.parse(
        localStorage.getItem(`uex-table-cache:${this._tableSettings.id}`)
      );
    } catch (e) {
      console.warn(e);
      this._cache = null;
    }
    if (!_.isEmpty(this._cache)) {
      if (this._cache.sort) {
        this.setSort(this._cache.sort, true);
      }
      if (this._cache.filter) {
        this.setFilter(this._cache.filter, true);
      }
      if (this._cache.pagination) {
        this.setPagination(this._cache.pagination, true);
      }
      this.onCacheLoaded.emit(this._cache);
    }
  }

  private _saveCache() {
    if (!this._tableSettings.id || !this._tableSettings.cache) {
      return;
    }
    this._cache = {
      sort: this.sort,
      filter: this.filter,
      pagination: this.pagination
    };
    try {
      localStorage.setItem(
        `uex-table-cache:${this._tableSettings.id}`,
        JSON.stringify(this._cache)
      );
    } catch (e) {
      console.warn(e);
      try {
        localStorage.removeItem(`uex-table-cache:${this._tableSettings.id}`);
      } catch (e) {}
    }
  }
}
